/**
 * Machine page › RMS.
 *
 * The demo's `rmsView(id)` (line 2196): the Remote Monitoring Service health
 * summary — overall index and per-system indices with their trend, the
 * seven-day alarm tally, operator-behaviour counters, and any open analyst
 * alert with its recommendation.
 *
 * Only the three live-data sites are connected, so 59 of the 100 machines have
 * a record. The rest get a plain statement of why, not an empty panel.
 *
 * The RMS feed identifies eleven machines by a vendor short code rather than
 * the fleet id; `rmsFor()` resolves that through `IDS`.
 *
 * UI layer.
 *
 * @see src/versions/v1/modules/fleet/MachineScene.transform.ts
 * @see docs/logic/fleet/machine-page.md
 */

import { useMemo } from 'react';
import { bandOf } from '../../schema/machine';
import { useOemData } from '../../shared/useOemData';
import { TabLoader } from './TabLoader';
import { rmsFor } from './MachineScene.transform';

export interface MachineRmsTabProps {
  machineId: string;
  /** Site display name, for the "not connected" explanation. */
  mineName: string;
  isLiveSite: boolean;
}

export function MachineRmsTab({ machineId, mineName, isLiveSite }: MachineRmsTabProps) {
  const { data, loading, error } = useOemData('rms');
  const record = useMemo(
    () => (data ? rmsFor(machineId, data.rms_health) : null),
    [data, machineId],
  );

  return (
    <TabLoader loading={loading} error={error} what="The RMS feed" rows={6}>
      {!record ? (
        <p className="text-sm text-muted">
          {machineId} is not connected to the Remote Monitoring Service.
          {isLiveSite
            ? ' Its class is monitored through telematics rather than RMS.'
            : ` ${mineName} has no underground network coverage on the working levels, so machine logs are consolidated by Pitram at the end of each shift instead of streaming to RMS.`}
        </p>
      ) : (
        <div className="@container space-y-6">
          <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Health summary
            </h3>
            <span className={`band-pill band-${record.health_index.band}`}>
              {record.health_index.overall}/100
            </span>
            <span className="text-[11px] text-muted">
              {record.rms_ref} · connected {record.connected_since} · {record.link} · last heard{' '}
              {record.last_heard.slice(11, 19)}
            </span>
            <span className="ml-auto text-[11px] text-muted">
              Analyst {record.analyst.split(' (')[0]}
            </span>
          </header>

          <div className="grid grid-cols-1 gap-6 @3xl:grid-cols-2">
            <section className="space-y-2">
              <h4 className="text-xs uppercase tracking-wider text-muted">Systems</h4>
              <ul className="space-y-2">
                {record.health_index.systems.map((system) => {
                  const band = bandOf(system.index);
                  return (
                    <li key={system.system} className="space-y-1">
                      <div className="flex items-baseline justify-between gap-3 text-xs">
                        <span className="text-zinc-900 dark:text-zinc-100">
                          {system.system} <span className="text-muted">· {system.trend}</span>
                        </span>
                        <span className="mono shrink-0">{system.index}</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${system.index}%`,
                            background: `var(--band-${band.key})`,
                          }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
              <p className="text-[11px] text-muted">{record.health_index.method}</p>
            </section>

            <section className="space-y-4">
              <div>
                <h4 className="text-xs uppercase tracking-wider text-muted">Alarms · 7 days</h4>
                {record.alarms_7d.length ? (
                  <ul className="mt-1 space-y-0.5 text-[11.5px]">
                    {record.alarms_7d.slice(0, 6).map((a) => (
                      <li key={a.code} className="flex gap-3">
                        <span className="mono">{a.code}</span>
                        <span>×{a.count}</span>
                        <span className="text-muted">
                          last {a.last.slice(5, 16).replace('T', ' ')}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1 text-sm text-muted">None.</p>
                )}
              </div>

              <div>
                <h4 className="text-xs uppercase tracking-wider text-muted">
                  Operator behaviour · 7 days
                </h4>
                <ul className="mt-1 space-y-0.5 text-[11.5px]">
                  {Object.entries(record.operator_behaviour_7d).map(([key, value]) => (
                    <li key={key} className="flex justify-between gap-3">
                      <span>{key.replace(/_/g, ' ')}</span>
                      <span className="mono">{value}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {record.open_alerts.length ? (
                <div>
                  <h4 className="text-xs uppercase tracking-wider text-muted">Open alert</h4>
                  {record.open_alerts.map((a) => (
                    <div key={a.ref} className="mt-1 text-[11.5px]">
                      <b className="mono">{a.ref}</b> · {a.issued.slice(0, 16).replace('T', ' ')}
                      <p>{a.signature}</p>
                      <p className="text-muted">
                        → {a.recommendation} · {a.status}
                      </p>
                    </div>
                  ))}
                </div>
              ) : null}
            </section>
          </div>

          {record.recommendations.length ? (
            <section>
              <h4 className="text-xs uppercase tracking-wider text-muted">Analyst notes</h4>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">
                {record.recommendations.map((r, i) => (
                  <li key={`${i}-${r.slice(0, 24)}`}>{r}</li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}
    </TabLoader>
  );
}
